import React from 'react';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';
// material
import { styled } from '@mui/material/styles';
import { Link, Stack, Typography, TextField, IconButton, InputAdornment, Box, Alert } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useNotificationsProvider } from '../../context/NotificationsContext';
// component
import Iconify from '../../components/Iconify';
import Logo from '../../components/Logo';

import { resetPasswordAccept } from './actions'

// ----------------------------------------------------------------------
const LoginStyle = styled('div')(({ theme }) => ({
  maxWidth: 480,
  margin: 'auto',
  display: 'flex',
  minHeight: '100vh',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(12, 0)
}));

const ContainerStyle = styled(Box)(({ theme }) => ({
  shadowColor: theme.palette.secondary,
  backgroundColor: theme.palette.grey,
  shadowOpacity: 0.8,
  shadowRadius: 2,
  shadowOffset: {
    height: 1,
    width: 1
  },
  elevation: 5,
}));

const LogoStyle = {
  width: 175,
  height: 48
};

export default function ResetPasswordAcceptForm() {
  const { t } = useTranslation()
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showNotification } = useNotificationsProvider();
  const [searchParams] = useSearchParams();

  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [error, setError] = React.useState(null);

  // Get token and email from URL params
  const token = searchParams.get('token');
  const email = searchParams.get('email');

  // Redirect if missing params
  React.useEffect(() => {
    if (!token || !email) {
      setError(t('reset-password-accept.missing-params'));
    }
  }, [token, email, t]);

  const ResetPasswordSchema = Yup.object().shape({
    email: Yup.string().email(t('reset-password-accept.invalid-email')).required(t('reset-password-accept.required-email')),
    password: Yup.string()
      .min(12, t('reset-password-accept.password-too-short'))
      .matches(/[A-Z]/, t('reset-password-accept.password-missing-uppercase'))
      .matches(/[a-z]/, t('reset-password-accept.password-missing-lowercase'))
      .matches(/[0-9]/, t('reset-password-accept.password-missing-number'))
      .matches(/[^A-Za-z0-9]/, t('reset-password-accept.password-missing-symbol'))
      .required(t('reset-password-accept.required-password')),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('password'), null], t('reset-password-accept.passwords-must-match'))
      .required(t('reset-password-accept.required-confirm-password'))
  });

  const formik = useFormik({
    initialValues: {
      email: email || '',
      password: '',
      confirmPassword: '',
    },
    enableReinitialize: true,
    validationSchema: ResetPasswordSchema,
    onSubmit: (data, { setSubmitting }) => {
      if (!token) {
        setError(t('reset-password-accept.missing-token'));
        setSubmitting(false);
        return;
      }
      setError(null);
      dispatch(resetPasswordAccept(token, data.email, data.password, (has2FA) => {
        setSubmitting(false);
        showNotification('success', t('reset-password-accept.success'));
        // Redirect to 2FA setup if not enabled, otherwise to dashboard
        if (has2FA) {
          navigate('/dashboard/app');
        } else {
          navigate('/setup-2fa?from=password-reset');
        }
      }, (translationKey) => {
        setSubmitting(false);
        setError(t(translationKey));
      }))
    },
  });

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps } = formik;

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };

  const handleShowConfirmPassword = () => {
    setShowConfirmPassword((show) => !show);
  };

  return (
    <LoginStyle>
      <ContainerStyle>
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <Stack alignItems="center">
                <Logo sx={LogoStyle} />
              </Stack>
              <Typography variant="h4">
                {t('reset-password-accept.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('reset-password-accept.subtitle')}
              </Typography>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <TextField
                fullWidth
                autoComplete="username"
                type="email"
                label={t('reset-password-accept.email-label')}
                {...getFieldProps('email')}
                error={Boolean(touched.email && errors.email)}
                helperText={touched.email && errors.email}
                disabled={!!email} // Disable if email comes from URL
              />

              <TextField
                fullWidth
                autoComplete="new-password"
                type={showPassword ? 'text' : 'password'}
                label={t('reset-password-accept.password-label')}
                {...getFieldProps('password')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleShowPassword} edge="end">
                        <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                error={Boolean(touched.password && errors.password)}
                helperText={touched.password && errors.password ? errors.password : t('reset-password-accept.password-requirements')}
              />

              <TextField
                fullWidth
                autoComplete="new-password"
                type={showConfirmPassword ? 'text' : 'password'}
                label={t('reset-password-accept.confirm-password-label')}
                {...getFieldProps('confirmPassword')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleShowConfirmPassword} edge="end">
                        <Iconify icon={showConfirmPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                error={Boolean(touched.confirmPassword && errors.confirmPassword)}
                helperText={touched.confirmPassword && errors.confirmPassword}
              />

              <Stack direction="column" alignItems="center" justifyContent="space-between" spacing={2} sx={{ my: 2 }}>
                <LoadingButton size="large" type="submit" variant="contained" loading={isSubmitting} disabled={!token}>
                  {t('reset-password-accept.submit-button')}
                </LoadingButton>
                <Link component={RouterLink} variant="subtitle2" to="/forgot-password" underline="hover">
                  {t('reset-password-accept.link-expired')}
                </Link>
                <Link component={RouterLink} variant="subtitle2" to="/login" underline="hover">
                  {t('reset-password-accept.back-to-login')}
                </Link>
              </Stack>
            </Stack>
          </Form>
        </FormikProvider>
      </ContainerStyle>
    </LoginStyle>
  );
};
